// @flow
import React, {useState} from 'react';
import {AutoSizer, Column, Table as RVTable} from 'react-virtualized';
import 'react-virtualized/styles.css';
import {
  IconButton,
  Divider,
  Typography,
  FormControlLabel,
  Checkbox,
  Grid,
  makeStyles,
} from '@material-ui/core';
import {
  AddCircleTwoTone,
  Add,
  Edit,
  VisibilityOutlined,
  VisibilityOffOutlined,
  InfoOutlined,
  Close,
} from '@material-ui/icons';
import MenuItem from '@material-ui/core/MenuItem';
import type {
  ProductVariantOption,
  ProductVariantOptionValue,
  Store,
} from '../../../../../entities';
import SelectVariantOptionModal from '../../ProductVariants/SelectVariantOptionModal/SelectVariantOptionModal';
import PremiumFeatureBadge from '../../../../../elements/PremiumFeatureBadge/PremiumFeatureBadge';
import CustomButton from '../../../../../elements/CustomButton/CustomButton';
import CustomOptionSelect from '../../ProductVariants/CustomOptionSelect/CustomOptionSelect';
import {ProductVariantOptionValueChip} from '../../ProductVariants/ProductVariantOptionValueChip/ProductVariantOptionValueChip';
import IosSwitch from '../../../../../elements/IosSwitch/IosSwitch';
import {getImageUrl} from '../../../../../utils/image_provider';
import CustomTextField from '../../../../../elements/CustomTextField/CustomTextField';
import {useIsFeatureInTier} from '../../../../../utils/component';
import {colors} from '../../../../../assets/styles/colors';
import type {ImageToCrop} from '../../../../../elements/CropModal';
import TiendadaTooltip from '../../../../../elements/tiendada-tooltip';
import CustomAlert from '../../../../../elements/CustomAlert/CustomAlert';

type ProductCriteria = {
  criterion: ProductVariantOption,
  variants: ProductVariantOptionValue[],
};

type VariantRow = {
  image: string | number,
  options: {[key: string]: string},
  price: string,
  taxedPrice: string,
  offerPrice?: string,
  taxedOfferPrice?: string,
  bulkPrice?: boolean,
  stock?: number,
  sku?: string,
  enabled?: boolean,
};

type Props = {
  productCriteria: ProductCriteria[],
  variantsShow: boolean,
  storeHasProductVariantsFeature: boolean,
  userHasProductVariantsFeatureDisabled: boolean,
  fetchProductVariantOptions: () => void,
  openSelectionModal: boolean,
  variantOptionToEdit: ?ProductVariantOption,
  variantOptionIndexEdit: ?number,
  tempSelectedValues: ProductVariantOptionValue[],
  cropResult: ImageToCrop[],
  handleOpenSelectionModal: (
    opt: ProductVariantOption,
    idx: number,
    vals?: ProductVariantOptionValue[]
  ) => void,
  handleCloseSelectionModal: () => void,
  handleChangeCriteriaVariants: (
    opt: ProductVariantOption,
    vals: ProductVariantOptionValue[],
    idx: number
  ) => void,
  handleAddCriteria: () => void,
  handleRemoveCriteria: (idx: number) => void,
  handleDeleteOptionValue: (valueIdx: number, critIdx: number) => void,
  handleVariantsShow: (e: SyntheticInputEvent<HTMLInputElement>) => void,
  globalProductVariantOptions: ProductVariantOption[],
  store: Store,
  getBasicDomain: (domains: string[]) => string,
  variantsTable: VariantRow[],
  handleChangeTaxedVariantPrice: (
    value: string,
    i: number,
    taxed?: boolean
  ) => void,
  handleChangeVariantPrice: (value: string, i: number) => void,
  handleChangeVariantTaxedOfferPrice: (
    value: string,
    i: number,
    taxed?: boolean
  ) => void,
  handleChangeVariantOfferPrice: (value: string, i: number) => void,
  handleChangeVariantBulkPrice: (value: boolean, i: number) => void,
  inventarioShow: boolean,
  handleChangeVariantStock: (value: number, i: number) => void,
  handleChangeVariantSKU: (value: string, i: number) => void,
  handleToggleVariantsRow: (i: number) => void,
  allowMixNMatch: boolean,
  handleAllowMixNMatch: (e: SyntheticInputEvent<HTMLInputElement>) => void,
  disabledAddMoreVariantOption: boolean,
  bulkPriceShow: boolean,
  handleVariantImageModalOpen: (i: number) => void,
  isSaving: boolean,
  variantSkuErrors: {[string]: any},
  validateVariantSku: (value: string, i: number) => void,
  setInventarioShow: (value: boolean) => void,
  errors: {[string]: any},
};

const useStyles = makeStyles(theme => ({
  contentSection: {
    display: 'flex',
    flexDirection: 'column',
    marginTop: theme.spacing(2),
    borderRadius: '12px',
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
    padding: theme.spacing(0, 1, 1, 1),
    [theme.breakpoints.up('sm')]: {
      padding: theme.spacing(0, 2, 2, 2),
    },
    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(0, 3, 3, 3),
    },
    background: 'white',
  },
  sectionTitle: {
    fontSize: '1rem',
    display: 'flex',
    alignItems: 'center',
    fontWeight: 600,
    marginTop: theme.spacing(3),
    [theme.breakpoints.down('xs')]: {
      marginTop: theme.spacing(2),
    },
  },
  sectionCheckbox: {
    '& .MuiFormControlLabel-label': {
      color: colors.grey700,
      fontSize: '0.875rem',
    },
  },
  divider: {
    margin: theme.spacing(2, 0),
  },
  optionsTitle: {
    marginTop: theme.spacing(2),
    fontWeight: 500,
    color: colors.grey700,
  },
  subtitle2: {
    marginLeft: 0,
    fontSize: '0.875rem',
    fontWeight: 600,
    marginTop: theme.spacing(2),
  },
  criteriaHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  addLineButton: {
    alignSelf: 'flex-start',
    color: colors.purple3,
  },
  variantNameInputWrap: {
    width: '100%!important',
    backgroundColor: '#FFFFFF!important',
    position: 'absolute',
    top: 221,
    height: '100%',
    left: 0,
    zIndex: 20,
    border: `1px solid ${colors.grey300}`,
  },
  addVariantOption: {
    display: 'flex',
    alignItems: 'center',
    color: colors.purple,
    gap: '8px',
    textDecoration: 'none',
    height: '100%',
  },
  wrapVariants: {
    padding: theme.spacing(1),
    border: `1px solid ${colors.grey300}`,
    display: 'flex',
    flexWrap: 'wrap',
    gap: '16px',
    width: '100%',
    height: '100%',
    borderRadius: '10px',
    position: 'relative',
  },
  variantsTableImage: {
    display: 'flex',
    height: 48,
    width: 48,
    minWidth: 48,
    justifyContent: 'center',
    alignSelf: 'center',
    alignItems: 'center',
    textAlign: 'center',
    borderRadius: '12px',
    cursor: 'pointer',
    overflow: 'hidden',
  },
  image: {
    height: 'auto',
    maxHeight: '100%',
    maxWidth: '100%',
    minWidth: '100%',
    minHeight: '100%',
    objectFit: 'contain',
  },
  variantsTableInputField: {
    width: '100%',
    '& input::-webkit-outer-spin-button': {
      display: 'none',
    },
    '& input::-webkit-inner-spin-button': {
      display: 'none',
    },
  },
  bulkPriceCheckbox: {
    '& span': {
      fontSize: '1rem',
      fontWeight: 600,
    },
  },
  headerCell: {
    backgroundColor: '#f5f5f5',
    fontWeight: 600,
    textTransform: 'none',
    padding: theme.spacing(1),
    margin: 0,
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '4px',

    '& .ReactVirtualized__Table__headerTruncatedText': {
      whiteSpace: 'normal',
      overflow: 'visible',
      textOverflow: 'inherit',
    },

    '&:first-child': {
      margin: 0,
    },
  },
  rowClassName: {
    paddingRight: '0 !important',
  },
  headerRow: {
    borderBottom: `2px solid ${colors.grey300}`,
  },
  variantsTableContainer: {
    marginTop: theme.spacing(2),
    height: 500,
    [theme.breakpoints.down('sm')]: {
      height: 400,
    },
    [theme.breakpoints.down('xs')]: {
      height: 350,
    },
    width: '100%',
    overflowX: 'auto',
    overflowY: 'hidden',
    border: `1px solid ${colors.grey300}`,
    borderRadius: '8px',
  },
  variantsTable: {
    minWidth: 800,
    height: '100%',

    '& .ReactVirtualized__Table__Grid': {
      overflowX: 'auto',
      overflowY: 'scroll',
      scrollbarGutter: 'stable',
      paddingBottom: '15rem',
    },

    '& .ReactVirtualized__Table__headerRow': {
      borderBottom: `2px solid ${colors.grey300}`,
    },
  },
}));

const MAX_VARIANTS_TABLE_LENGTH = 1000;

export default function VariantsSection(props: Props) {
  const {
    productCriteria,
    variantsShow,
    storeHasProductVariantsFeature,
    userHasProductVariantsFeatureDisabled,
    fetchProductVariantOptions,
    openSelectionModal,
    variantOptionToEdit,
    variantOptionIndexEdit,
    tempSelectedValues,
    handleOpenSelectionModal,
    handleCloseSelectionModal,
    handleChangeCriteriaVariants,
    handleAddCriteria,
    handleRemoveCriteria,
    handleDeleteOptionValue,
    handleVariantsShow,
    globalProductVariantOptions,
    store,
    getBasicDomain,
    variantsTable,
    handleChangeTaxedVariantPrice,
    handleChangeVariantPrice,
    handleChangeVariantTaxedOfferPrice,
    handleChangeVariantOfferPrice,
    handleChangeVariantBulkPrice,
    handleChangeVariantStock,
    handleChangeVariantSKU,
    handleToggleVariantsRow,
    allowMixNMatch,
    handleAllowMixNMatch,
    disabledAddMoreVariantOption,
    bulkPriceShow,
    handleVariantImageModalOpen,
    isSaving,
    variantSkuErrors,
    validateVariantSku,
    errors,
    cropResult,
    inventarioShow,
    setInventarioShow,
  } = props;

  const [showStockAlert, setShowStockAlert] = useState(true);

  const classes = useStyles();

  const hasTaxesFeature = useIsFeatureInTier('TAXES');
  const rowGetter = ({index}) => variantsTable[index];

  const variantsTableLength = variantsTable.length;

  const isMaxVariantsTableLength =
    variantsTableLength >= MAX_VARIANTS_TABLE_LENGTH;

  return (
    <div className={classes.contentSection} style={{marginBottom: 120}}>
      {openSelectionModal &&
        variantOptionToEdit &&
        variantOptionIndexEdit != null && (
          <SelectVariantOptionModal
            open
            handleClose={handleCloseSelectionModal}
            variantOption={variantOptionToEdit}
            selectedOptions={tempSelectedValues}
            onConfirm={newValues => {
              handleChangeCriteriaVariants(
                variantOptionToEdit,
                newValues,
                variantOptionIndexEdit
              );
              handleCloseSelectionModal();
            }}
          />
        )}

      <Typography className={classes.sectionTitle} id="product-variants">
        Variantes de producto{' '}
        {!storeHasProductVariantsFeature && <PremiumFeatureBadge />}
      </Typography>
      <FormControlLabel
        control={
          <Checkbox
            checked={variantsShow}
            disabled={
              !storeHasProductVariantsFeature ||
              userHasProductVariantsFeatureDisabled
            }
            onChange={handleVariantsShow}
            color="primary"
          />
        }
        className={classes.sectionCheckbox}
        label="Este producto tiene múltiples opciones como diferentes tallas, tamaños o colores."
      />

      {variantsShow && (
        <>
          <Divider className={classes.divider} />
          <Typography className={classes.optionsTitle}>OPCIONES</Typography>
          {productCriteria.map((criterion, i) => (
            <React.Fragment key={i}>
              <Grid container spacing={2} alignItems="flex-end">
                <Grid item xs={4}>
                  <Typography className={classes.subtitle2}>
                    {`Opción ${i + 1}`}
                  </Typography>
                </Grid>
                <Grid item xs={8}>
                  <div className={classes.criteriaHeader}>
                    <div />
                    <CustomButton onClick={() => handleRemoveCriteria(i)}>
                      Borrar
                    </CustomButton>
                  </div>
                </Grid>
              </Grid>
              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <CustomOptionSelect
                    options={globalProductVariantOptions}
                    hiddenOptions={productCriteria.map(
                      el => el.criterion.label
                    )}
                    value={criterion.criterion.label}
                    openCallback={fetchProductVariantOptions}
                    onSelectOption={value => handleOpenSelectionModal(value, i)}
                    label={`Selecciona un valor para la opción ${i + 1}`}
                    disabled={isMaxVariantsTableLength}
                  >
                    {({handleClose}) => (
                      <MenuItem
                        className={classes.variantNameInputWrap}
                        disableRipple
                        onClick={handleClose}
                      >
                        <a
                          href={`https://${getBasicDomain(
                            store?.domains || []
                          )}/admin/products-variants`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={classes.addVariantOption}
                        >
                          <Add />
                          <Typography>Crear nuevo</Typography>
                        </a>
                      </MenuItem>
                    )}
                  </CustomOptionSelect>
                </Grid>
                <Grid item xs={8}>
                  <div className={classes.wrapVariants}>
                    {criterion.variants.length > 0 && (
                      <IconButton
                        color="primary"
                        size="small"
                        className={classes.editVariantOptionsBtn}
                        disabled={isMaxVariantsTableLength}
                        onClick={() =>
                          handleOpenSelectionModal(
                            criterion.criterion,
                            i,
                            criterion.variants
                          )
                        }
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                    )}
                    {criterion.variants.map((el, vIndex) => (
                      <ProductVariantOptionValueChip
                        key={el.id}
                        vov={el}
                        imageShape={criterion.criterion.imageShape}
                        displayType={criterion.criterion.displayType}
                        onDelete={() =>
                          criterion.variants.length === 1
                            ? handleRemoveCriteria(i)
                            : handleDeleteOptionValue(vIndex, i)
                        }
                      />
                    ))}
                  </div>
                </Grid>
              </Grid>
            </React.Fragment>
          ))}
          <CustomButton
            onClick={handleAddCriteria}
            className={classes.addLineButton}
            startIcon={<AddCircleTwoTone />}
            disabled={disabledAddMoreVariantOption || isMaxVariantsTableLength}
          >
            Agregar otra opción
          </CustomButton>

          {isMaxVariantsTableLength && (
            <CustomAlert
              open
              className={classes.alert}
              severity="warning"
              action={<Close fontSize="inherit" />}
            >
              Has alcanzado el límite de variantes permitidas.
            </CustomAlert>
          )}

          <Divider className={classes.divider} />

          {variantsTableLength > 0 && (
            <CustomAlert
              open={showStockAlert}
              className={classes.alert}
              severity={inventarioShow ? 'info' : 'warning'}
              action={
                <IconButton
                  aria-label="close"
                  color="inherit"
                  size="small"
                  onClick={() => {
                    setShowStockAlert(false);
                  }}
                >
                  <Close fontSize="inherit" />
                </IconButton>
              }
            >
              <div>
                {inventarioShow ? (
                  <>
                    El inventario está <strong>activado</strong>. Ahora puedes
                    editar el stock de cada variante y se descontará
                    automáticamente cuando se realicen ventas.
                  </>
                ) : (
                  <>
                    Para editar el stock de cada variante, primero{' '}
                    <strong>activa el inventario</strong> del producto. Si el
                    inventario está desactivado, el stock no se descontará
                    automáticamente al completar las órdenes.
                  </>
                )}
              </div>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={inventarioShow}
                    onChange={e => {
                      setInventarioShow(e.target.checked);
                    }}
                    name="checkedI"
                    color="primary"
                  />
                }
                className={classes.sectionCheckbox}
                label={
                  inventarioShow
                    ? 'Desactivar inventario'
                    : 'Activar inventario'
                }
              />
            </CustomAlert>
          )}

          <div className={classes.variantsTableContainer}>
            <div className={classes.variantsTable}>
              <AutoSizer disableHeight>
                {({width}) => (
                  <RVTable
                    width={width}
                    height={500}
                    headerHeight={70}
                    rowHeight={70}
                    className={classes.variantsTable}
                    rowCount={variantsTable.length}
                    rowGetter={rowGetter}
                    headerClassName={classes.headerCell}
                    rowClassName={classes.rowClassName}
                  >
                    <Column
                      label="Variante"
                      dataKey="options"
                      className={classes.bodyCell}
                      headerClassName={classes.headerCell}
                      width={220}
                      flexGrow={1}
                      cellRenderer={({rowData, rowIndex}) => {
                        const imageIsNew = typeof rowData.image === 'number';
                        let variantImage = getImageUrl('');
                        if (imageIsNew && typeof rowData.image === 'number') {
                          const croppedImage = cropResult[rowData.image];

                          if (croppedImage) {
                            variantImage = croppedImage.preview;
                          }
                        } else if (typeof rowData.image === 'string') {
                          variantImage = getImageUrl(rowData.image);
                        }

                        return (
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '.25rem',
                            }}
                          >
                            <div className={classes.variantsTableImage}>
                              <img
                                src={variantImage}
                                className={classes.image}
                                alt={Object.values(rowData.options).join('/')}
                                onClick={() =>
                                  handleVariantImageModalOpen(rowIndex)
                                }
                              />
                            </div>
                            <span
                              style={{
                                fontSize: '0.75rem',
                                color: colors.grey900,
                                wordBreak: 'break-word',
                                whiteSpace: 'pre-wrap',
                                overflowWrap: 'break-word',
                              }}
                            >
                              {Object.values(rowData.options).join('/')}
                            </span>
                          </div>
                        );
                      }}
                    />
                    <Column
                      flexGrow={1}
                      label="Valor de venta"
                      dataKey="price"
                      width={150}
                      className={classes.bodyCell}
                      headerClassName={classes.headerCell}
                      cellRenderer={({rowData, rowIndex}) => (
                        <CustomTextField
                          className={classes.variantsTableInputField}
                          type="number"
                          value={rowData.price}
                          disabled={isSaving || !rowData.enabled}
                          onChange={e => {
                            handleChangeVariantPrice(e.target.value, rowIndex);
                          }}
                        />
                      )}
                    />
                    <Column
                      flexGrow={1}
                      label="Precio de venta"
                      dataKey="taxedPrice"
                      width={150}
                      className={classes.bodyCell}
                      headerClassName={classes.headerCell}
                      cellRenderer={({rowData, rowIndex}) => (
                        <CustomTextField
                          className={classes.variantsTableInputField}
                          type="number"
                          value={rowData.taxedPrice}
                          disabled={
                            !hasTaxesFeature || isSaving || !rowData.enabled
                          }
                          onChange={e =>
                            handleChangeTaxedVariantPrice(
                              e.target.value,
                              rowIndex
                            )
                          }
                        />
                      )}
                    />
                    <Column
                      flexGrow={1}
                      label="Oferta"
                      dataKey="offerPrice"
                      width={150}
                      className={classes.bodyCell}
                      headerClassName={classes.headerCell}
                      cellRenderer={({rowData, rowIndex}) => (
                        <CustomTextField
                          className={classes.variantsTableInputField}
                          type="number"
                          value={rowData.offerPrice || ''}
                          disabled={
                            isSaving || !rowData.enabled || allowMixNMatch
                          }
                          onChange={e =>
                            handleChangeVariantOfferPrice(
                              e.target.value,
                              rowIndex
                            )
                          }
                        />
                      )}
                    />
                    <Column
                      flexGrow={1}
                      label="Precio oferta"
                      dataKey="taxedOfferPrice"
                      width={150}
                      className={classes.bodyCell}
                      headerClassName={classes.headerCell}
                      cellRenderer={({rowData, rowIndex}) => (
                        <CustomTextField
                          className={classes.variantsTableInputField}
                          type="number"
                          value={rowData.taxedOfferPrice || ''}
                          disabled={
                            !hasTaxesFeature ||
                            isSaving ||
                            !rowData.enabled ||
                            allowMixNMatch
                          }
                          onChange={e =>
                            handleChangeVariantTaxedOfferPrice(
                              e.target.value,
                              rowIndex
                            )
                          }
                        />
                      )}
                    />
                    {bulkPriceShow && (
                      <Column
                        flexGrow={1}
                        label="Mayorista"
                        dataKey="bulkPrice"
                        width={80}
                        className={classes.bodyCell}
                        headerClassName={classes.headerCell}
                        cellRenderer={({rowData, rowIndex}) => (
                          <Checkbox
                            checked={!!rowData.bulkPrice}
                            disabled={isSaving || !rowData.enabled}
                            onChange={e =>
                              handleChangeVariantBulkPrice(
                                e.target.checked,
                                rowIndex
                              )
                            }
                          />
                        )}
                      />
                    )}
                    <Column
                      flexGrow={1}
                      label="Stock"
                      headerRenderer={() => (
                        <div className={classes.headerCell}>
                          <span>Stocks</span>
                          {!inventarioShow && (
                            <TiendadaTooltip
                              title="Activa el inventario para poder editar el stock de cada variante."
                              placement="top"
                            >
                              <InfoOutlined
                                color="primary"
                                className={classes.infoBtn}
                              />
                            </TiendadaTooltip>
                          )}
                        </div>
                      )}
                      dataKey="stock"
                      width={120}
                      className={classes.bodyCell}
                      headerClassName={classes.headerCell}
                      cellRenderer={({rowData, rowIndex}) => (
                        <CustomTextField
                          className={classes.variantsTableInputField}
                          type="number"
                          value={rowData.stock || ''}
                          disabled={isSaving || !inventarioShow}
                          onChange={e =>
                            handleChangeVariantStock(
                              parseInt(e.target.value, 10),
                              rowIndex
                            )
                          }
                          error={!!errors[`variant-stock-${rowIndex}`]}
                          helperText={
                            errors[`variant-stock-${rowIndex}`]?.message
                          }
                        />
                      )}
                    />
                    <Column
                      flexGrow={1}
                      label="SKU"
                      dataKey="sku"
                      width={120}
                      className={classes.bodyCell}
                      headerClassName={classes.headerCell}
                      cellRenderer={({rowData, rowIndex}) => (
                        <CustomTextField
                          className={classes.variantsTableInputField}
                          type="text"
                          value={rowData.sku || ''}
                          disabled={isSaving || !rowData.enabled}
                          error={!!variantSkuErrors[`variant-sku-${rowIndex}`]}
                          helperText={
                            variantSkuErrors[`variant-sku-${rowIndex}`]
                          }
                          onBlur={e =>
                            validateVariantSku(e.target.value, rowIndex)
                          }
                          onChange={e =>
                            handleChangeVariantSKU(e.target.value, rowIndex)
                          }
                        />
                      )}
                    />
                    <Column
                      dataKey="enabled"
                      width={80}
                      className={classes.bodyCell}
                      headerClassName={classes.headerCell}
                      cellRenderer={({rowIndex}) => (
                        <IconButton
                          onClick={() => handleToggleVariantsRow(rowIndex)}
                        >
                          {variantsTable[rowIndex].enabled ? (
                            <VisibilityOutlined />
                          ) : (
                            <VisibilityOffOutlined />
                          )}
                        </IconButton>
                      )}
                    />
                  </RVTable>
                )}
              </AutoSizer>
            </div>
          </div>

          {bulkPriceShow &&
            variantsTable.filter(r => r.bulkPrice).length > 1 && (
              <FormControlLabel
                control={
                  <IosSwitch
                    checked={allowMixNMatch}
                    onChange={handleAllowMixNMatch}
                    name="mixNMatch"
                  />
                }
                className={classes.bulkPriceCheckbox}
                label="Permitir combinación de variantes en compras al por mayor?"
              />
            )}
        </>
      )}
    </div>
  );
}
